import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X, Check, Crown, Mic, Sparkles, Globe, Clock, Zap } from "lucide-react";

export default function SubscribeModal({ isOpen, onClose, onSubscribed, blockReason }) {
  const [selectedPlan, setSelectedPlan] = useState("pro");

  const plans = [
    {
      id: "standard",
      name: "Standard",
      price: "$2.99",
      period: "/month",
      features: [
        { icon: Mic, text: "100 transcription minutes/month" },
        { icon: Sparkles, text: "25 document edits/month" },
        { icon: Globe, text: "Auto-fix errors" },
        { icon: Clock, text: "Tone transformation" },
      ],
      description: "Perfect for personal use",
      icon: Zap,
      color: "#3b82f6"
    },
    {
      id: "pro",
      name: "Pro",
      price: "$4.99",
      period: "/month",
      features: [
        { icon: Mic, text: "Unlimited transcription" },
        { icon: Sparkles, text: "Unlimited document edits" },
        { icon: Globe, text: "Auto-fix errors" },
        { icon: Clock, text: "Tone transformation" },
      ],
      description: "For power users & professionals",
      icon: Crown,
      color: "#8b5cf6",
      popular: true
    }
  ];

  if (!isOpen) return null;

  const getBlockTitle = () => {
    switch (blockReason) {
      case "trial_ended":
        return "Your Free Trial Has Ended 🎉";
      case "limit_reached":
        return "You've Reached Your Limit";
      case "subscription_inactive":
        return "Subscription Inactive";
      default:
        return "Choose Your Plan";
    }
  };

  const getBlockMessage = () => {
    switch (blockReason) {
      case "trial_ended":
        return "We hope you enjoyed Voice Capture! Subscribe to continue using all features — unlimited transcription, auto-fix, tone changes, and translation.";
      case "limit_reached":
        return "You've used all your free minutes. Choose a plan that works best for you.";
      case "subscription_inactive":
        return "Your subscription is no longer active. Resubscribe to continue using Voice Capture.";
      default:
        return "Select the plan that works best for your needs.";
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-50" style={{ backgroundColor: 'rgba(0, 0, 0, 0.9)' }} />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="relative w-full max-w-3xl rounded-2xl p-6" style={{ backgroundColor: '#1a2332', border: '1px solid #2d3748' }}>
          
          {/* Close button - only if not a hard block */}
          {!blockReason && (
            <button 
              onClick={onClose} 
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          )}

          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: '#6366f120' }}>
              <Crown className="h-8 w-8" style={{ color: '#6366f1' }} />
            </div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#f5f5f5' }}>
              {getBlockTitle()}
            </h2>
            <p className="text-sm" style={{ color: '#a0aec0' }}>
              {getBlockMessage()}
            </p>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {plans.map((plan) => (
              <div
                key={plan.id}
                onClick={() => setSelectedPlan(plan.id)}
                className={`relative p-4 rounded-xl cursor-pointer transition-all ${
                  selectedPlan === plan.id ? 'ring-2' : ''
                }`}
                style={{
                  backgroundColor: '#252d3d',
                  border: selectedPlan === plan.id ? `2px solid ${plan.color}` : '1px solid #2d3748',
                  ringColor: plan.color
                }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="px-3 py-1 text-xs font-medium rounded-full" style={{ backgroundColor: plan.color, color: '#fff' }}>
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                {/* Plan Header */}
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${plan.color}20`, color: plan.color }}>
                    <plan.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-bold" style={{ color: '#f5f5f5' }}>{plan.name}</h3>
                    <p className="text-xs" style={{ color: '#718096' }}>{plan.description}</p>
                  </div>
                </div>

                {/* Price */}
                <div className="mb-4">
                  <span className="text-2xl font-bold" style={{ color: '#f5f5f5' }}>{plan.price}</span>
                  <span className="text-sm" style={{ color: '#718096' }}>{plan.period}</span>
                </div>

                {/* Features */}
                <ul className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm" style={{ color: '#a0aec0' }}>
                      <Check className="w-4 h-4" style={{ color: '#22c55e' }} />
                      {feature.text}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Subscribe Button - Coming Soon */}
          <Button
            disabled={true}
            className="w-full py-6 text-lg font-semibold rounded-xl cursor-not-allowed"
            style={{ backgroundColor: '#4b5563', color: '#9ca3af' }}
          >
            Subscribe to {plans.find(p => p.id === selectedPlan)?.name} - {plans.find(p => p.id === selectedPlan)?.price}/year - Coming Soon
          </Button>

          {/* Alternative action for blocked users */}
          {blockReason && (
            <div className="mt-4 text-center">
              <p className="text-xs mb-2" style={{ color: '#718096' }}>
                Payments are being set up. Check back soon!
              </p>
              <button 
                onClick={() => {
                  // Allow them to continue with limited functionality or log out
                  if (onClose) onClose();
                }}
                className="text-sm underline hover:no-underline"
                style={{ color: '#6366f1' }}
              >
                Continue with limited access
              </button>
            </div>
          )}

          {/* Footer */}
          <p className="text-center text-xs mt-4" style={{ color: '#718096' }}>
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>
      </div>
    </>
  );
}
